import { useState, useCallback, useEffect } from 'react';
import {
  runSolBalanceQueryForCommonQueries,
  runTokenBalanceQueryForCommonQueries,
} from '@/lib/collections/commonQueries';
import {
  SOL,
  BONK,
  SKR,
  MEW,
  USDC,
  ANGELIC_SOL,
  GMT,
  DRAGON,
  getDecimalsForMint,
} from '@/utils/tokenConfig';

export interface TokenBalances {
  [mint: string]: number; // human-readable amounts (divided by 10^decimals)
}

/**
 * Fetches human-readable balances for all supported tokens (SOL, BONK, SKR, MEW, USDC, ANGEL, GMT, DRAGON).
 * Returns amounts already divided by 10^decimals — ready for display.
 * Refreshes every 30 seconds.
 */
export function useTokenBalances(walletAddress: string | undefined): {
  balances: TokenBalances;
  loading: boolean;
  refetch: () => void;
} {
  const [balances, setBalances] = useState<TokenBalances>({});
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!walletAddress) {
      setBalances({});
      return;
    }
    setLoading(true);
    try {
      const [solBal, bonkBal, skrBal, mewBal, usdcBal, angelBal, gmtBal, dragonBal] = await Promise.allSettled([
        runSolBalanceQueryForCommonQueries(`sol_${walletAddress}`, { walletAddress }),
        runTokenBalanceQueryForCommonQueries(`bonk_${walletAddress}`, { walletAddress, tokenMint: BONK }),
        runTokenBalanceQueryForCommonQueries(`skr_${walletAddress}`, { walletAddress, tokenMint: SKR }),
        runTokenBalanceQueryForCommonQueries(`mew_${walletAddress}`, { walletAddress, tokenMint: MEW }),
        runTokenBalanceQueryForCommonQueries(`usdc_${walletAddress}`, { walletAddress, tokenMint: USDC }),
        runTokenBalanceQueryForCommonQueries(`angel_${walletAddress}`, { walletAddress, tokenMint: ANGELIC_SOL }),
        runTokenBalanceQueryForCommonQueries(`gmt_${walletAddress}`, { walletAddress, tokenMint: GMT }),
        runTokenBalanceQueryForCommonQueries(`dragon_${walletAddress}`, { walletAddress, tokenMint: DRAGON }),
      ]);

      const toHuman = (rawVal: number, mint: string): number => {
        const divisor = Math.pow(10, getDecimalsForMint(mint));
        return rawVal / divisor;
      };

      setBalances({
        [SOL]: solBal.status === 'fulfilled' ? toHuman(Number(solBal.value ?? 0), SOL) : 0,
        [BONK]: bonkBal.status === 'fulfilled' ? toHuman(Number(bonkBal.value ?? 0), BONK) : 0,
        [SKR]: skrBal.status === 'fulfilled' ? toHuman(Number(skrBal.value ?? 0), SKR) : 0,
        [MEW]: mewBal.status === 'fulfilled' ? toHuman(Number(mewBal.value ?? 0), MEW) : 0,
        [USDC]: usdcBal.status === 'fulfilled' ? toHuman(Number(usdcBal.value ?? 0), USDC) : 0,
        [ANGELIC_SOL]: angelBal.status === 'fulfilled' ? toHuman(Number(angelBal.value ?? 0), ANGELIC_SOL) : 0,
        [GMT]: gmtBal.status === 'fulfilled' ? toHuman(Number(gmtBal.value ?? 0), GMT) : 0,
        [DRAGON]: dragonBal.status === 'fulfilled' ? toHuman(Number(dragonBal.value ?? 0), DRAGON) : 0,
      });
    } catch {
      // ignore — individual query errors are already caught by allSettled
    } finally {
      setLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30_000);
    return () => clearInterval(id);
  }, [fetchAll]);

  return { balances, loading, refetch: fetchAll };
}
